import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { optionalTrue, searchPatch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import type { TrainingAssignment } from "./-components/assignment";
import { AssignCourseDialog } from "./-components/assign-course-dialog";
import { AssignmentProgress } from "./-components/assignment-progress";
import { RosterTable } from "./-components/roster-table";

export const Route = createFileRoute("/_authenticated/_workforce/training/roster")({
  validateSearch: (search: Record<string, unknown>): { all?: true } => ({
    all: optionalTrue(search.all),
  }),
  component: TrainingRoster,
});

/** Who still owes training, per course, with the actions to fix it. Opens on what is
 * outstanding. */
function TrainingRoster() {
  const { facilityId } = useFacility();
  const canManage = useCan("training:manage");
  const queryClient = useQueryClient();
  const { all: showAllParam } = Route.useSearch();
  const showAll = showAllParam === true;
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const setShowAll = (next: boolean) => patch({ all: next ? true : undefined });
  const [assignOpen, setAssignOpen] = useState(false);
  const [removing, setRemoving] = useState<TrainingAssignment | null>(null);

  const assignments = useQuery(
    orpc.workforce.training.listAssignments.queryOptions({ input: { facilityId } }),
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.training.key() });

  const reset = useMutation(
    orpc.workforce.training.resetAttempts.mutationOptions({
      onSuccess: async () => {
        toast.success("Attempts reset. They can take the quiz again");
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const remove = useMutation(
    orpc.workforce.training.removeAssignment.mutationOptions({
      onSuccess: async () => {
        toast.success("Assignment removed");
        setRemoving(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = assignments.data ?? [];
  const outstanding = rows.filter((row) => row.state !== "completed");
  const overdue = outstanding.filter((row) => row.state === "overdue").length;
  const visible = showAll ? rows : outstanding;
  const failed = outstanding.filter((row) => row.state === "failed").length;
  // The overview's own count: whatever is neither started nor late is unopened.
  const inProgress = outstanding.filter((row) => row.state === "in_progress").length;
  const counts = {
    total: rows.length,
    completed: rows.length - outstanding.length,
    overdue,
    inProgress,
    notStarted: outstanding.length - inProgress - overdue,
  };
  const buildingChosen = facilityId !== "all";

  const meta = assignments.isError
    ? "The roster could not be loaded"
    : assignments.isPending
      ? "Loading…"
      : outstanding.length === 0
        ? "Nobody has outstanding training"
        : overdue > 0
          ? `${overdue} overdue`
          : "Nothing overdue";

  return (
    <>
      <PageHeader
        title="Who owes training"
        count={assignments.data ? visible.length : undefined}
        meta={meta}
        actions={
          canManage ? (
            <Button onClick={() => setAssignOpen(true)} disabled={!buildingChosen}>
              Assign a course
            </Button>
          ) : null
        }
      />

      {/* Why "Assign a course" is off, said beside the work rather than in a
          full-width card that outweighed the roster under it. */}
      {canManage && !buildingChosen ? (
        <p className="text-muted-foreground text-sm">
          Choose a single building to assign training. An assignment belongs to one building.
        </p>
      ) : null}

      {/* The whole roster at a glance before the rows: how much is done, under
          way, unopened and late, each count inside its own fill. */}
      {assignments.data && rows.length > 0 ? (
        <AssignmentProgress counts={counts} failed={failed} showAction={false} />
      ) : null}

      {assignments.isError ? (
        <ReadFailed
          message="Could not load the roster. This is not a statement that everybody is up to date."
          error={assignments.error}
          onRetry={() => void assignments.refetch()}
          retrying={assignments.isFetching}
        />
      ) : assignments.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing has been assigned"
          description="Assign a course to start tracking who has done what."
        />
      ) : (
        <RosterTable
          rows={visible}
          empty={
            <EmptyState
              title="Nothing outstanding"
              description="Everyone has finished what they were assigned. Assign a course to add more."
            />
          }
          chips={
            <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
              <legend className="sr-only">Show</legend>
              <FilterChip
                selected={!showAll}
                count={outstanding.length}
                onClick={() => setShowAll(false)}
              >
                Outstanding
              </FilterChip>
              <FilterChip selected={showAll} count={rows.length} onClick={() => setShowAll(true)}>
                Everything
              </FilterChip>
            </fieldset>
          }
          canManage={canManage}
          busy={reset.isPending || remove.isPending}
          onReset={(assignmentId) => reset.mutate({ assignmentId })}
          onRemove={setRemoving}
        />
      )}

      {buildingChosen ? (
        <AssignCourseDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          facilityId={facilityId}
        />
      ) : null}

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removing
                ? `Remove ${removing.courseTitle} from ${removing.learnerName || "this employee"}?`
                : "Remove this assignment?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removing
                ? `${removing.learnerName || "This employee"} will no longer be asked to complete it. Any progress they have made goes with it.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep assignment</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate({ assignmentId: removing.id })}
            >
              Remove assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
